import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchServicesMunicipaux,
  getAllServicesMunicipaux,
  getServiceById,
  createServiceMunicipal,
  updateServiceMunicipal,
  deleteServiceMunicipal,
  toggleServiceMunicipalActive,
  toggleServiceMunicipalPublic,
} from "../../../../services/servicesMunicipauxServices";

import { SERVICES_MUNICIPAUX_INITIAL_FORM } from "../data/servicesMunicipauxAdminData";

import {
  buildServicesMunicipauxSearchParams,
  getServicesMunicipauxStats,
  normalizeServiceMunicipalForm,
  slugify,
  validateServiceMunicipalForm,
} from "../utils/servicesMunicipauxAdminUtils";

export default function useServicesMunicipauxAdmin() {
  const mountedRef = useRef(true);

  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [publicFilter, setPublicFilter] = useState("all");
  const [withGps, setWithGps] = useState("all");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState({ ...SERVICES_MUNICIPAUX_INITIAL_FORM });
  const [fieldErrors, setFieldErrors] = useState({});

  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit) || 1, 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getServicesMunicipauxStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    window.clearTimeout(showNotice.timeout);
    showNotice.timeout = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildServicesMunicipauxSearchParams({
        q,
        categorie,
        typeFilter,
        stateFilter,
        publicFilter,
        withGps,
        page,
        limit,
      });

      const res = await searchServicesMunicipaux(params);

      const noFilters =
        !q &&
        !categorie &&
        typeFilter === "all" &&
        stateFilter === "all" &&
        publicFilter === "all" &&
        withGps === "all";

      if ((!res?.items?.length || res?.total === 0) && noFilters && page === 1) {
        const fallback = await getAllServicesMunicipaux();
        const fallbackItems = Array.isArray(fallback) ? fallback : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        return;
      }

      if (!mountedRef.current) return;

      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (fetchError) {
      console.error("Erreur chargement services municipaux :", fetchError);

      try {
        const fallback = await getAllServicesMunicipaux();
        const fallbackItems = Array.isArray(fallback) ? fallback : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        setError(
          "Le chargement administrateur a échoué. Affichage d’une liste de secours.",
        );
      } catch (fallbackError) {
        console.error("Erreur fallback services municipaux :", fallbackError);

        if (!mountedRef.current) return;

        setItems([]);
        setTotal(0);
        setError("Impossible de charger les services municipaux pour le moment.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    q,
    categorie,
    typeFilter,
    stateFilter,
    publicFilter,
    withGps,
    page,
    limit,
  ]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeCategorie = useCallback((value) => {
    setCategorie(value);
    setPage(1);
  }, []);

  const changeTypeFilter = useCallback((value) => {
    setTypeFilter(value);
    setPage(1);
  }, []);

  const changeStateFilter = useCallback((value) => {
    setStateFilter(value);
    setPage(1);
  }, []);

  const changePublicFilter = useCallback((value) => {
    setPublicFilter(value);
    setPage(1);
  }, []);

  const changeWithGps = useCallback((value) => {
    setWithGps(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 24);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setCategorie("");
    setTypeFilter("all");
    setStateFilter("all");
    setPublicFilter("all");
    setWithGps("all");
    setLimit(24);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm({ ...SERVICES_MUNICIPAUX_INITIAL_FORM });
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getServiceById(id);

      if (!row) {
        setError("Service municipal introuvable.");
        return;
      }

      setCurrentId(id);
      setForm({
        nom_service: row.nom_service || "",
        slug_service: row.slug_service || "",
        categorie_service: row.categorie_service || "",
        type_service: row.type_service || "",
        parent_id:
          row.parent_id !== null && row.parent_id !== undefined
            ? String(row.parent_id)
            : "",
        accroche_service: row.accroche_service || "",
        description_service: row.description_service || "",
        adresse_service: row.adresse_service || "",
        cp_service: row.cp_service || "",
        ville_service: row.ville_service || "",
        gps_service: row.gps_service || "",
        tel_service: row.tel_service || "",
        tel_secondaire: row.tel_secondaire || "",
        mail_service: row.mail_service || "",
        website_service: row.website_service || "",
        autre_url_service: row.autre_url_service || "",
        horaires_service: row.horaires_service || "",
        infos_complementaires: row.infos_complementaires || "",
        icone_service: row.icone_service || "",
        ordre_affichage:
          row.ordre_affichage !== null && row.ordre_affichage !== undefined
            ? Number(row.ordre_affichage) || 0
            : 0,
        is_public: !!row.is_public,
        is_active: !!row.is_active,
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture service municipal :", editError);
      setError("Impossible d’ouvrir ce service municipal.");
    } finally {
      setSaving(false);
    }
  }, []);

  const openEditFromPreview = useCallback(async () => {
    if (!previewItem?.id) return;

    const id = previewItem.id;
    setPreviewItem(null);
    await openEdit(id);
  }, [openEdit, previewItem]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm({ ...SERVICES_MUNICIPAUX_INITIAL_FORM });
    setFieldErrors({});
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => {
      if (field === "nom_service") {
        const previousSlug = previous.slug_service || "";
        const generatedPreviousSlug = slugify(previous.nom_service || "");

        const shouldAutoUpdateSlug =
          !previousSlug || previousSlug === generatedPreviousSlug;

        return {
          ...previous,
          nom_service: value,
          slug_service: shouldAutoUpdateSlug
            ? slugify(value)
            : previous.slug_service,
        };
      }

      return {
        ...previous,
        [field]: value,
      };
    });

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateServiceMunicipalForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setError("");

      const payload = normalizeServiceMunicipalForm(form);

      if (currentId) {
        await updateServiceMunicipal(currentId, payload);
        showNotice("Service municipal modifié avec succès.");
      } else {
        await createServiceMunicipal(payload);
        showNotice("Service municipal créé avec succès.");
      }

      closeForm();
      await fetchData();
    } catch (saveError) {
      console.error("Erreur enregistrement service municipal :", saveError);
      setError("Impossible d’enregistrer ce service municipal.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggleActive = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`active-${id}`);
        setError("");

        await toggleServiceMunicipalActive(id);
        showNotice("Statut actif mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement statut actif :", toggleError);
        setError("Impossible de modifier le statut actif.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const onTogglePublic = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`public-${id}`);
        setError("");

        await toggleServiceMunicipalPublic(id);
        showNotice("Visibilité publique mise à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement visibilité publique :", toggleError);
        setError("Impossible de modifier la visibilité publique.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openPreview = useCallback(
    async (id) => {
      if (!id) return;

      const localItem = items.find((item) => item.id === id);

      if (localItem) {
        setPreviewItem(localItem);
        return;
      }

      try {
        setActionId(`preview-${id}`);

        const row = await getServiceById(id);

        if (row) {
          setPreviewItem(row);
        }
      } catch (previewError) {
        console.error("Erreur aperçu service municipal :", previewError);
        setError("Impossible d’afficher l’aperçu de ce service municipal.");
      } finally {
        setActionId(null);
      }
    },
    [items],
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          nom_service: "Ce service municipal",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteServiceMunicipal(deleteItem.id);

      setDeleteItem(null);
      showNotice("Service municipal supprimé.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression service municipal :", deleteError);
      setError("Impossible de supprimer ce service municipal.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  return {
    q,
    setQ,
    categorie,
    typeFilter,
    stateFilter,
    publicFilter,
    withGps,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,

    loading,
    saving,
    deleting,
    actionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    previewItem,
    deleteItem,

    fetchData,
    onSubmitSearch,
    changeCategorie,
    changeTypeFilter,
    changeStateFilter,
    changePublicFilter,
    changeWithGps,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    openEditFromPreview,
    closeForm,
    updateFormField,
    onSave,

    onToggleActive,
    onTogglePublic,

    openPreview,
    closePreview,

    openDelete,
    closeDelete,
    onDelete,
  };
}